/**
 * Post-auth redirect — persists a redirect URL across the auth boundary.
 *
 * When a user clicks "Sign in" or "Sign up" from a share page (or any
 * deep-linked page), the redirect target is stored in sessionStorage.
 * After auth completes (login-form, auth callback, or onboarding finish),
 * the stored redirect is consumed and the user lands back where they started.
 *
 * sessionStorage is used instead of URL params because the signup flow
 * traverses multiple pages (signup → verify-email → email link → /auth/callback
 * → onboarding) and URL params would be lost at each hop.
 *
 * The login form also supports `?redirect=` as a URL param for direct login,
 * which takes precedence over sessionStorage.
 */

const STORAGE_KEY = 'virza-post-auth-redirect';

// Only allow safe relative paths — no protocol-relative, no absolute URLs
const SAFE_PATH_RE = /^\/(?:[a-zA-Z0-9_\-.~!$&'()*+,;=:@/]|%[0-9A-Fa-f]{2})*$/;

function isSafeRedirect(url: string): boolean {
  if (!url || typeof url !== 'string') return false;
  if (!url.startsWith('/') || url.startsWith('//')) return false;
  return SAFE_PATH_RE.test(url);
}

/** Store a redirect URL to be consumed after auth completes. */
export function storePostAuthRedirect(path: string): void {
  if (!isSafeRedirect(path)) return;
  try {
    sessionStorage.setItem(STORAGE_KEY, path);
  } catch {
    // Storage unavailable (SSR, private browsing quota)
  }
}

/**
 * Consume the stored redirect URL (single-use).
 * Returns the path if valid, or null if nothing was stored.
 */
export function consumePostAuthRedirect(): string | null {
  try {
    const stored = sessionStorage.getItem(STORAGE_KEY);
    sessionStorage.removeItem(STORAGE_KEY);
    if (stored && isSafeRedirect(stored)) return stored;
  } catch {
    // Storage unavailable
  }
  return null;
}

/** Clear a stored redirect without consuming the value. */
export function clearPostAuthRedirect(): void {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // Storage unavailable
  }
}
